/**
 * The red «несвязанные ученики» alert above the register (T.Z20b) — the pure half.
 *
 * Framework-free on purpose, like every model here: the `unit` Playwright project
 * imports it in plain Node, so the decision "is there a bucket to shout about,
 * which one, and where does the click land" is data, and `UnlinkedAlert.tsx` only
 * renders it.
 *
 * The owner amendment (2026-09): the dashboard's «Несвязанные» card (T.Z20) is a
 * number nobody has to act on; what the owner wants surfaced is the OFFLINE
 * applications that have no площадка and are already standing in the funnel — the
 * pupils a person has to attach a venue to today. Those are two buckets of the
 * frozen `#3`:
 *
 *   `LINK_RECEIVED` («Получил ссылку») — the link went out, the venue did not;
 *   `EVI_PASSED`    («Прошёл ЕВИ»)     — the pupil cleared the competition, the
 *                                        venue still did not follow.
 *
 * Both are read with the same request the alert's own drill-down answers, so the
 * banner and the list it opens can never disagree (the T.Z20 argument, kept).
 */

// Dependency-free by the same rule `applications-model.ts` follows: this map is
// re-exported from `../dashboard/status`, whose only import is type-only.
import { STATUS_LABEL } from '../dashboard/status';

// `formatCount` (thousand groups, NBSP) is dependency-free and deterministic too —
// the same helper the dashboard's cards printed their counts with.
import { formatCount } from '../dashboard/format';

import type { ApplicationListQuery, CourseFormat } from '../../api/applications';

/**
 * The two candidate buckets, in funnel order. The alert names the FIRST non-empty
 * one — a «Получил ссылку» pupil is earlier in the funnel than a «Прошёл ЕВИ»
 * one, so that is the queue to work first — and renders ONE alert, never two.
 */
export const UNLINKED_ALERT_STATUSES = ['LINK_RECEIVED', 'EVI_PASSED'] as const;

export type UnlinkedAlertStatus = (typeof UNLINKED_ALERT_STATUSES)[number];

/** The fixed halves of the drill-down: no площадка, офлайн — the owner's preset. */
export const UNLINKED_ALERT_HAS_VENUE = false;
export const UNLINKED_ALERT_FORMAT: CourseFormat = 'OFFLINE';

/** `data-testid` of the whole alert (the click target). */
export const UNLINKED_ALERT_TEST_ID = 'unlinked-alert';
/** `data-testid` of the sentence — the text an assertion pins. */
export const UNLINKED_ALERT_TEXT_TEST_ID = 'unlinked-alert-text';

/** The two `#3` count queries the alert reads, in funnel order. */
export const UNLINKED_ALERT_QUERIES: ReadonlyArray<ApplicationListQuery> =
  UNLINKED_ALERT_STATUSES.map((status) => ({
    hasVenue: UNLINKED_ALERT_HAS_VENUE,
    format: UNLINKED_ALERT_FORMAT,
    status,
  }));

/**
 * The drill-down URL of one bucket — `/applications?hasVenue=false&format=OFFLINE&status=<CODE>`,
 * exactly the preset the owner quoted. Built here (not at the call site) so the
 * URL and the count query above can never drift apart.
 */
export function unlinkedAlertHref(status: UnlinkedAlertStatus): string {
  const search = new URLSearchParams({
    hasVenue: String(UNLINKED_ALERT_HAS_VENUE),
    format: UNLINKED_ALERT_FORMAT,
    status,
  });
  return `/applications?${search.toString()}`;
}

/** The sentence the alert prints. The status speaks in its RU label, not its code. */
export function unlinkedAlertText(total: number, label: string): string {
  return `Найдены несвязанные ученики: ${formatCount(total)} офлайн со статусом „${label}"`;
}

/** What the alert component needs per bucket: one `#3` count, or the absence of one. */
export interface UnlinkedAlertBucket {
  status: UnlinkedAlertStatus;
  /** `meta.total` of the slice; `undefined` while the fetch is in flight or failed. */
  total?: number | null;
}

export interface UnlinkedAlertState {
  status: UnlinkedAlertStatus;
  /** The bucket's RU label („Получил ссылку" / „Прошёл ЕВИ"). */
  label: string;
  /** The bucket's `meta.total` — a count arrives only when the alert is visible. */
  total: number;
  /** The whole sentence, ready to print. */
  text: string;
  /** Where the click lands (`react-router` `<Link to>`). */
  href: string;
  /** The hover/tooltip: the alert is a shortcut, the filter bar is the long way. */
  hint: string;
  testId: string;
}

/** A count that is not a sane count is nothing to shout about. */
function saneTotal(total: number | null | undefined): number | null {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return null;
  return Math.round(total);
}

/**
 * The first non-empty bucket, in funnel order — or `null` when there is nothing
 * to surface. Both buckets empty (and both counts absent, in flight or failed)
 * render NO alert: the banner may only ever speak when `#3` has answered with a
 * positive total for the very preset its link carries.
 */
export function unlinkedAlert(
  buckets: ReadonlyArray<UnlinkedAlertBucket>,
): UnlinkedAlertState | null {
  for (let index = 0; index < UNLINKED_ALERT_STATUSES.length; index += 1) {
    const status = UNLINKED_ALERT_STATUSES[index]!;
    const bucket = buckets.find((candidate) => candidate.status === status);
    if (!bucket) continue;
    const total = saneTotal(bucket.total);
    if (total === null) continue;

    const label = STATUS_LABEL[status] ?? status;
    return {
      status,
      label,
      total,
      text: unlinkedAlertText(total, label),
      href: unlinkedAlertHref(status),
      hint: `Открыть реестр: без площадки, офлайн, ${label}`,
      testId: UNLINKED_ALERT_TEST_ID,
    };
  }
  return null;
}
